import { Link } from "react-router-dom";
import { PageActions, PageHeader, PageTitle } from "./PageHeader";
import { BackButton } from "./BackButton";

type PageLayoutProps = {
  title: string;
  subtitle?: string;
  backHref?: string;
  backLabel?: string;
  backAction?: "history";
  actions?: React.ReactNode;
  logoAlt?: string;
  variant?: "default" | "centered";
  softBackdrop?: boolean;
  centeredSurface?: "card" | "none";
  mobileCenteredSurface?: "card" | "none";
  children: React.ReactNode;
};

export function PageLayout({
  title,
  subtitle,
  backHref,
  backLabel,
  backAction,
  actions,
  logoAlt,
  variant = "default",
  softBackdrop = false,
  centeredSurface = "card",
  mobileCenteredSurface = "card",
  children,
}: PageLayoutProps) {
  const isCentered = variant === "centered";
  const isCenteredCardless = isCentered && centeredSurface === "none";
  const isCenteredMobileCardless =
    isCentered && centeredSurface === "card" && mobileCenteredSurface === "none";

  return (
    <div
      className={`relative mx-auto flex w-full flex-col ${
        isCentered
          ? isCenteredCardless
            ? "max-w-[480px] items-center gap-5 px-4 pb-8 pt-8 sm:px-5 sm:pb-10 sm:pt-10"
            : isCenteredMobileCardless
              ? "max-w-3xl items-center gap-5 px-4 pb-8 pt-8 sm:px-5 md:gap-8 md:px-0"
            : "max-w-3xl items-center gap-8"
          : "max-w-5xl gap-8"
      }`}
    >
      {softBackdrop ? (
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-x-0 top-0 h-[320px] bg-[radial-gradient(110%_90%_at_50%_0%,rgba(83,74,183,0.14)_0%,rgba(83,74,183,0.05)_38%,rgba(255,255,255,0)_74%)] ${
            isCenteredMobileCardless ? "md:hidden" : ""
          }`}
        />
      ) : null}
      <header
        className={`relative flex w-full flex-col ${
          isCentered ? "items-center gap-2 text-center" : "gap-4"
        }`}
      >
        {logoAlt ? (
          <img
            src="/logo-sidebar.svg"
            alt={logoAlt}
            className={`h-auto w-[168px] object-contain sm:w-[180px] ${
              isCentered ? "mx-auto" : ""
            }`}
          />
        ) : null}
        <PageHeader className={isCentered ? "page-header--centered" : ""}>
          <PageTitle>
            <div className={isCentered ? "text-center" : "text-start"}>
              <div
                className={
                  isCentered
                    ? isCenteredMobileCardless
                      ? "text-[1.95rem] font-bold leading-[1.15] text-[color:var(--text-primary)] sm:text-[2.1rem] md:text-3xl md:font-semibold md:leading-normal lg:text-4xl"
                      : "text-3xl font-semibold text-[color:var(--text-primary)] sm:text-4xl"
                    : "text-2xl font-semibold text-[color:var(--text-primary)] sm:text-3xl"
                }
              >
                {title}
              </div>
              {subtitle ? (
                <p
                  className={`mt-2 text-sm ${
                    isCentered
                      ? isCenteredMobileCardless
                        ? "text-[13px] text-[color:var(--text-muted)] sm:text-sm md:text-base md:text-[color:var(--text-secondary)]"
                        : "text-[color:var(--text-secondary)] sm:text-base"
                      : "text-[color:var(--text-muted)]"
                  }`}
                >
                  {subtitle}
                </p>
              ) : null}
            </div>
          </PageTitle>
          <PageActions>
            {backAction === "history" && backLabel ? (
              <BackButton label={backLabel} fallbackHref={backHref} />
            ) : backHref && backLabel ? (
              <Link
                to={backHref}
                className="w-fit text-sm font-semibold text-[color:var(--text-muted)]"
              >
                {backLabel}
              </Link>
            ) : null}
            {actions ? <div>{actions}</div> : null}
          </PageActions>
        </PageHeader>
      </header>
      <section
        className={`relative w-full ${
          isCentered
            ? isCenteredCardless
              ? "mx-auto max-w-[460px]"
              : isCenteredMobileCardless
                ? "mx-auto max-w-[640px] md:a2z-card-soft md:overflow-hidden md:rounded-2xl md:p-8 md:shadow-[var(--shadow-md)]"
              : "a2z-card-soft mx-auto max-w-[640px] overflow-hidden rounded-2xl p-5 shadow-[var(--shadow-md)] sm:p-10"
            : "a2z-card p-4 sm:p-6"
        }`}
      >
        {softBackdrop && !isCenteredCardless ? (
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-0 bg-[linear-gradient(145deg,rgba(83,74,183,0.06),rgba(245,158,11,0.03)_55%,transparent_100%)] ${
              isCenteredMobileCardless ? "hidden md:block" : ""
            }`}
          />
        ) : null}
        <div className="relative">{children}</div>
      </section>
    </div>
  );
}
